import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingComponent from '../../../../components/LoadingComponent';
import { FeedbackContext } from '../../../../contexts/FeedbackContext';
import { financialEntryApi } from '../api';
import {
  detailToForm,
  formToSplitCreatePayload,
  formToUpdatePayload,
  getMeaningfulFormItems,
} from '../mappers';
import { useFinancialFormActions } from '../FinancialFormActionsContext';
import GeneralInfoSection from './GeneralInfoSection';
import PaymentSection from './PaymentSection';
import ItemsSection from './ItemsSection';
import {
  defaultFormSchemaValues,
  financialEntryFormSchema,
  FinancialEntryFormSchemaType,
} from './schema';
import { financialEntryRoutes } from '../routes';
import { FormContainer, FormFieldsScope } from './style';
import {
  getInstallmentValidationMessages,
  getItemValidationMessages,
} from './formValidation';

const FinancialEntryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const { setSaveHandler, setSaving } = useFinancialFormActions();
  const [loading, setLoading] = useState(!!id);

  const isEdit = Boolean(id);
  const isCreate = !id;

  const methods = useForm<FinancialEntryFormSchemaType>({
    resolver: zodResolver(financialEntryFormSchema),
    defaultValues: defaultFormSchemaValues,
  });

  const {
    handleSubmit,
    reset,
    getValues,
  } = methods;

  useEffect(() => {
    if (!id) return;

    const loadEntry = async () => {
      try {
        setLoading(true);
        const detail = await financialEntryApi.getById(Number(id));
        reset(detailToForm(detail));
      } catch {
        setFeedback({
          message: 'Erro ao carregar lançamento.',
          isOpen: true,
          statusOk: false,
        });
        navigate(financialEntryRoutes.list);
      } finally {
        setLoading(false);
      }
    };

    loadEntry();
  }, [id, navigate, reset, setFeedback]);

  const uploadPendingFiles = async (
    items: FinancialEntryFormSchemaType['items'],
    savedItems: Array<{ id?: number }>,
  ) => {
    const meaningfulItems = getMeaningfulFormItems(items);

    for (let i = 0; i < meaningfulItems.length; i++) {
      const pending = meaningfulItems[i].pendingFiles ?? [];
      const savedItemId = meaningfulItems[i].id ?? savedItems[i]?.id;
      if (!savedItemId || pending.length === 0) continue;

      await financialEntryApi.uploadFiles(
        savedItemId,
        pending.map((p) => p.file),
      );
    }
  };

  const onSubmit = useCallback(async (values: FinancialEntryFormSchemaType) => {
    try {
      setSaving(true);

      if (isCreate) {
        const payload = formToSplitCreatePayload(values);
        const result = await financialEntryApi.createSplit(payload);

        const primaryItems = result.primaryEntry?.items ?? [];
        await uploadPendingFiles(values.items, primaryItems);

        const createdCount = result.entries?.length ?? 1;
        setFeedback({
          message:
            createdCount > 1
              ? `${createdCount} lançamentos criados com sucesso.`
              : 'Lançamento criado com sucesso.',
          isOpen: true,
          statusOk: true,
        });
        navigate(financialEntryRoutes.list);
        return;
      }

      const updated = await financialEntryApi.update(
        Number(id),
        formToUpdatePayload(values),
      );

      await uploadPendingFiles(values.items, updated.items);

      setFeedback({
        message: 'Lançamento atualizado com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      navigate(financialEntryRoutes.list);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        (isCreate ? 'Erro ao criar lançamento.' : 'Erro ao atualizar lançamento.');
      setFeedback({ message, isOpen: true, statusOk: false });
    } finally {
      setSaving(false);
    }
  }, [id, isCreate, navigate, setFeedback, setSaving]);

  useEffect(() => {
    setSaveHandler(() => {
      handleSubmit(onSubmit, (formErrors) => {
        const values = getValues();
        const sectionMessages = [
          ...getInstallmentValidationMessages(
            values.installments,
            formErrors.installments,
          ),
          ...getItemValidationMessages(values.items, formErrors.items),
        ];

        setFeedback({
          message:
            sectionMessages.length > 0
              ? sectionMessages.slice(0, 3).join(' ')
              : 'Preencha os campos obrigatórios antes de salvar.',
          isOpen: true,
          statusOk: false,
        });
      })();
    });
    return () => setSaveHandler(null);
  }, [getValues, handleSubmit, onSubmit, setSaveHandler, setFeedback]);

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormFieldsScope>
          <FormContainer>
            <GeneralInfoSection />
            <PaymentSection isEdit={isEdit} />
            <ItemsSection />
          </FormContainer>
        </FormFieldsScope>
      </form>
    </FormProvider>
  );
};

export default FinancialEntryForm;
